// Port of Source SDK 2013 gamemovement.cpp (FullWalkMove) as implemented in prototype/bhop.html.
// Tick order and formulas: docs/MOVEMENT.md. Behavior must stay tick-identical to the prototype;
// tests/movement.test.ts and tests/prototypeParity.test.ts guard it.
//
// No allocations in here: everything works on module-level scratch vectors.

import {
  DIST_EPSILON,
  DT,
  EYE_DUCK,
  EYE_STAND,
  HULL_HEIGHT_DUCK,
  HULL_HEIGHT_STAND,
  NON_JUMP_VELOCITY,
  type Cvars,
} from './constants';
import {
  CONTENTS_LADDER,
  CONTENTS_SOLID,
  MASK_WATER,
  MASK_PLAYERSOLID,
  TraceResult,
  type CollisionWorld,
} from './collision/types';
import { IN_BACK, IN_DUCK, IN_FORWARD, IN_JUMP, IN_MOVELEFT, IN_MOVERIGHT, type UserCmd } from './input';
import { HULL_MAXS_STAND, HULL_MINS, horizontalSpeed, hullMaxs, type PlayerState } from './player';
import { copy3, dot3, type Vec3 } from './vec';

const wishdir: Vec3 = [0, 0, 0];
const end: Vec3 = [0, 0, 0];
const tmpPos: Vec3 = [0, 0, 0];
const p1: Vec3 = [0, 0, 0];
const v1: Vec3 = [0, 0, 0];
const p2: Vec3 = [0, 0, 0];
const v2: Vec3 = [0, 0, 0];
const origVel: Vec3 = [0, 0, 0];
const planes: Vec3[] = [
  [0, 0, 0],
  [0, 0, 0],
  [0, 0, 0],
  [0, 0, 0],
];
const tr = new TraceResult();

function traceHull(world: CollisionWorld, p: PlayerState, start: Vec3, dx: number, dy: number, dz: number) {
  end[0] = start[0] + dx;
  end[1] = start[1] + dy;
  end[2] = start[2] + dz;
  return world.trace(start, end, HULL_MINS, hullMaxs(p), MASK_PLAYERSOLID, tr);
}

function friction(p: PlayerState, cv: Cvars) {
  const v = p.vel;
  // Argument order matches the prototype (three.js x, y, z = Source -Y, Z, -X) so the result is bit-identical.
  const speed = Math.hypot(v[1], v[2], v[0]);
  if (speed < 0.1) return;
  const control = speed < cv.stopspeed ? cv.stopspeed : speed;
  const drop = control * cv.friction * DT;
  const ns = Math.max(speed - drop, 0) / speed;
  v[0] *= ns;
  v[1] *= ns;
  v[2] *= ns;
}

function accelerate(p: PlayerState, wd: Vec3, ws: number, accel: number) {
  const add = ws - dot3(p.vel, wd);
  if (add <= 0) return;
  let as = accel * DT * ws;
  if (as > add) as = add;
  p.vel[0] += as * wd[0];
  p.vel[1] += as * wd[1];
}

function airAccelerate(p: PlayerState, wd: Vec3, ws: number, accel: number, aircap: number) {
  const capped = Math.min(ws, aircap);
  const add = capped - dot3(p.vel, wd);
  if (add <= 0) return;
  let as = accel * ws * DT;
  if (as > add) as = add;
  p.vel[0] += as * wd[0];
  p.vel[1] += as * wd[1];
}

function clipVelocity(v: Vec3, n: Vec3) {
  const back = dot3(v, n);
  v[0] -= n[0] * back;
  v[1] -= n[1] * back;
  v[2] -= n[2] * back;
  // Source ClipVelocity: "iterate once to make sure we aren't still moving through the plane".
  // On sloped planes rounding can leave a tiny push into the surface, and the next trace then stops
  // at once (frozen on terrain). Exactly 0 on axis-aligned planes, so the built-in course is unchanged.
  const adjust = dot3(v, n);
  if (adjust < 0) {
    v[0] -= n[0] * adjust;
    v[1] -= n[1] * adjust;
    v[2] -= n[2] * adjust;
  }
}

const crease: Vec3 = [0, 0, 0];

/** Up to 4 bumps, clipping velocity against every plane hit (overbounce 1). Moves `pos`/`vel` in place. */
function slideMove(world: CollisionWorld, p: PlayerState, pos: Vec3, vel: Vec3, dt: number) {
  let time = dt;
  let numPlanes = 0;
  let moved = false;
  let blocked = false;
  copy3(origVel, vel);
  for (let bump = 0; bump < 4; bump++) {
    const dx = vel[0] * time;
    const dy = vel[1] * time;
    const dz = vel[2] * time;
    if (dx === 0 && dy === 0 && dz === 0) break;
    const t = traceHull(world, p, pos, dx, dy, dz);
    copy3(pos, t.endPos);
    if (t.fraction > 0) moved = true;
    if (t.fraction >= 1) break;
    blocked = true;
    const n = t.planeNormal;
    if (t.fraction === 0 && numPlanes > 0 && dot3(n, planes[numPlanes - 1]) > 0.999) {
      // An almost coplanar neighbour (terrain triangles meeting at a shallow angle) that we are
      // already within DIST_EPSILON of: every trace would stop at once ("ramp bug"). Step off the
      // surface a hair and go on. Never happens on axis-aligned geometry.
      pos[0] += n[0] * DIST_EPSILON;
      pos[1] += n[1] * DIST_EPSILON;
      pos[2] += n[2] * DIST_EPSILON;
      moved = true;
      continue;
    }
    time *= 1 - t.fraction;
    copy3(planes[numPlanes++], t.planeNormal);
    clipVelocity(vel, t.planeNormal);
    for (let i = 0; i < numPlanes; i++) if (dot3(vel, planes[i]) < 0) clipVelocity(vel, planes[i]);
    let stuck = false;
    for (let i = 0; i < numPlanes; i++) if (dot3(vel, planes[i]) < -1e-6) stuck = true;
    if (stuck && numPlanes === 2) {
      // Source: two planes that can't both be satisfied: slide along their crease.
      const a = planes[0];
      const b = planes[1];
      crease[0] = a[1] * b[2] - a[2] * b[1];
      crease[1] = a[2] * b[0] - a[0] * b[2];
      crease[2] = a[0] * b[1] - a[1] * b[0];
      const len = Math.hypot(crease[0], crease[1], crease[2]);
      if (len > 1e-6) {
        const d = dot3(crease, vel) / (len * len);
        vel[0] = crease[0] * d;
        vel[1] = crease[1] * d;
        vel[2] = crease[2] * d;
        stuck = false;
      }
    }
    if (stuck || dot3(vel, origVel) <= 0) {
      vel[0] = vel[1] = vel[2] = 0;
      break;
    }
  }
  // Source TryPlayerMove: blocked without moving at all (wedged in a crevice): stop, so gravity
  // does not pile up speed in place.
  if (blocked && !moved) vel[0] = vel[1] = vel[2] = 0;
}

function stayOnGround(world: CollisionWorld, p: PlayerState, cv: Cvars) {
  const t = traceHull(world, p, p.pos, 0, 0, -cv.stepsize);
  if (t.fraction < 1 && t.planeNormal[2] >= 0.7 && t.fraction > 0) p.pos[2] = t.endPos[2];
}

function walkMove(world: CollisionWorld, p: PlayerState, cv: Cvars, wd: Vec3, ws: number) {
  const pos = p.pos;
  const vel = p.vel;
  vel[2] = 0;
  accelerate(p, wd, ws, cv.accelerate);
  vel[2] = 0;
  const spd = horizontalSpeed(p);
  if (spd > cv.maxspeed) {
    const r = cv.maxspeed / spd;
    vel[0] *= r;
    vel[1] *= r;
  }
  // Push triggers: move with the base velocity added (no-op when there is none).
  const bx = p.baseVel[0];
  const by = p.baseVel[1];
  const hasBase = bx !== 0 || by !== 0;
  if (hasBase) {
    vel[0] += bx;
    vel[1] += by;
  } else if (spd < 1) {
    vel[0] = vel[1] = 0;
    return;
  }
  const dx = vel[0] * DT;
  const dy = vel[1] * DT;
  const direct = traceHull(world, p, pos, dx, dy, 0);
  if (direct.fraction >= 1) {
    copy3(pos, direct.endPos);
    if (hasBase) {
      vel[0] -= bx;
      vel[1] -= by;
    }
    stayOnGround(world, p, cv);
    return;
  }
  // Try a plain slide and a step-up slide, keep whichever gets further.
  copy3(p1, pos);
  copy3(v1, vel);
  slideMove(world, p, p1, v1, DT);
  copy3(p2, pos);
  copy3(v2, vel);
  const up = traceHull(world, p, p2, 0, 0, cv.stepsize);
  p2[2] = up.endPos[2];
  slideMove(world, p, p2, v2, DT);
  const rise = p2[2] - pos[2];
  const dn = traceHull(world, p, p2, 0, 0, -(rise + 1));
  let useStep = false;
  if (dn.fraction < 1 && dn.planeNormal[2] >= 0.7) {
    p2[2] = dn.endPos[2];
    const d1 = Math.hypot(p1[0] - pos[0], p1[1] - pos[1]);
    const d2 = Math.hypot(p2[0] - pos[0], p2[1] - pos[1]);
    useStep = d2 > d1 + 0.01;
  }
  if (useStep) {
    copy3(pos, p2);
    vel[0] = v2[0];
    vel[1] = v2[1];
  } else {
    copy3(pos, p1);
    vel[0] = v1[0];
    vel[1] = v1[1];
  }
  vel[2] = 0;
  if (hasBase) {
    vel[0] -= bx;
    vel[1] -= by;
  }
  stayOnGround(world, p, cv);
}

function categorizePosition(world: CollisionWorld, p: PlayerState) {
  if (p.vel[2] > NON_JUMP_VELOCITY) {
    p.ground = null;
    return;
  }
  const t = traceHull(world, p, p.pos, 0, 0, -2);
  if (t.fraction < 1 && t.planeNormal[2] >= 0.7) {
    p.ground = t.entity;
    p.pos[2] = t.endPos[2];
  } else p.ground = null;
}

function duck(world: CollisionWorld, p: PlayerState, wantDuck: boolean) {
  const diff = HULL_HEIGHT_STAND - HULL_HEIGHT_DUCK;
  if (wantDuck && !p.ducked) {
    if (!p.ground) {
      // Air duck pulls the legs up, the head stays put.
      p.pos[2] += diff;
      p.prev[2] += diff;
      p.fx.eyeShift -= diff;
    }
    p.ducked = true;
  } else if (!wantDuck && p.ducked) {
    if (p.ground) {
      if (!world.testPosition(p.pos, HULL_MINS, HULL_MAXS_STAND, MASK_PLAYERSOLID)) p.ducked = false;
    } else {
      tmpPos[0] = p.pos[0];
      tmpPos[1] = p.pos[1];
      tmpPos[2] = p.pos[2] - diff;
      if (!world.testPosition(tmpPos, HULL_MINS, HULL_MAXS_STAND, MASK_PLAYERSOLID)) {
        copy3(p.pos, tmpPos);
        p.prev[2] -= diff;
        p.fx.eyeShift += diff;
        p.ducked = false;
      } else if (!world.testPosition(p.pos, HULL_MINS, HULL_MAXS_STAND, MASK_PLAYERSOLID)) p.ducked = false;
    }
  }
}

/* ---------------- Ladders (Source CGameMovement::LadderMove / FullLadderMove) ---------------- */

const LADDER_DISTANCE = 2;
const MAX_CLIMB_SPEED = 200;
const LADDER_JUMP_SPEED = 270;
const LADDER_NONE = 0;
const LADDER_ON = 1;
const LADDER_JUMPED_OFF = 2;
const ladderTr = new TraceResult();
const ladderEnd: Vec3 = [0, 0, 0];
const floorPoint: Vec3 = [0, 0, 0];
const POINT: Vec3 = [0, 0, 0];

/**
 * Looks for a ladder in the direction the player is moving (or the ladder they're already on) and
 * sets the ladder velocity. Uses the full view direction, pitch included: looking up and pressing
 * forward climbs, looking down descends.
 */
function ladderMove(world: CollisionWorld, p: PlayerState, cmd: Readonly<UserCmd>): number {
  const sy = Math.sin(cmd.yaw);
  const cy = Math.cos(cmd.yaw);
  const sp = Math.sin(cmd.pitch);
  const cp = Math.cos(cmd.pitch);
  // forward = (cp*cy, cp*sy, -sp), right = (sy, -cy, 0)
  let wx: number;
  let wy: number;
  let wz: number;
  if (p.ladder) {
    wx = -p.ladderNormal[0];
    wy = -p.ladderNormal[1];
    wz = -p.ladderNormal[2];
  } else {
    const fm = cmd.forwardmove;
    const sm = cmd.sidemove;
    if (fm === 0 && sm === 0) return LADDER_NONE;
    wx = cp * cy * fm + sy * sm;
    wy = cp * sy * fm - cy * sm;
    wz = -sp * fm;
    const len = Math.hypot(wx, wy, wz);
    if (len === 0) return LADDER_NONE;
    wx /= len;
    wy /= len;
    wz /= len;
  }
  ladderEnd[0] = p.pos[0] + wx * LADDER_DISTANCE;
  ladderEnd[1] = p.pos[1] + wy * LADDER_DISTANCE;
  ladderEnd[2] = p.pos[2] + wz * LADDER_DISTANCE;
  const t = world.trace(p.pos, ladderEnd, HULL_MINS, hullMaxs(p), MASK_PLAYERSOLID, ladderTr);
  if (t.fraction === 1 || (t.contents & CONTENTS_LADDER) === 0) return LADDER_NONE;

  p.ladder = true;
  const n = t.planeNormal;
  copy3(p.ladderNormal, n);

  floorPoint[0] = p.pos[0];
  floorPoint[1] = p.pos[1];
  floorPoint[2] = p.pos[2] + HULL_MINS[2] - 1;
  const onFloor = p.ground !== null || world.testPosition(floorPoint, POINT, POINT, CONTENTS_SOLID);

  const b = cmd.buttons;
  let forwardSpeed = 0;
  let rightSpeed = 0;
  if (b & IN_BACK) forwardSpeed -= MAX_CLIMB_SPEED;
  if (b & IN_FORWARD) forwardSpeed += MAX_CLIMB_SPEED;
  if (b & IN_MOVELEFT) rightSpeed -= MAX_CLIMB_SPEED;
  if (b & IN_MOVERIGHT) rightSpeed += MAX_CLIMB_SPEED;

  if (b & IN_JUMP) {
    p.ladder = false;
    p.vel[0] = n[0] * LADDER_JUMP_SPEED;
    p.vel[1] = n[1] * LADDER_JUMP_SPEED;
    p.vel[2] = n[2] * LADDER_JUMP_SPEED;
    return LADDER_JUMPED_OFF;
  }
  if (forwardSpeed === 0 && rightSpeed === 0) {
    p.vel[0] = p.vel[1] = p.vel[2] = 0;
    return LADDER_ON;
  }
  // Intended velocity from the full view direction.
  const vx = cp * cy * forwardSpeed + sy * rightSpeed;
  const vy = cp * sy * forwardSpeed - cy * rightSpeed;
  const vz = -sp * forwardSpeed;
  // perp = normalize(up x normal): the horizontal direction along the ladder face.
  let px = -n[1];
  let py = n[0];
  const pl = Math.hypot(px, py);
  if (pl > 0) {
    px /= pl;
    py /= pl;
  }
  // Velocity into the ladder face becomes velocity up the ladder: vel = lateral - normal * (n x perp).
  const into = vx * n[0] + vy * n[1] + vz * n[2];
  const lx = vx - n[0] * into;
  const ly = vy - n[1] * into;
  const lz = vz - n[2] * into;
  // n x perp, with perp[2] = 0
  const cx = -n[2] * py;
  const cyy = n[2] * px;
  const cz = n[0] * py - n[1] * px;
  p.vel[0] = lx - into * cx;
  p.vel[1] = ly - into * cyy;
  p.vel[2] = lz - into * cz;
  if (onFloor && into > 0) {
    // On the ground moving away from the ladder: step off.
    p.vel[0] += MAX_CLIMB_SPEED * n[0];
    p.vel[1] += MAX_CLIMB_SPEED * n[1];
    p.vel[2] += MAX_CLIMB_SPEED * n[2];
  }
  return LADDER_ON;
}

/** Move on a ladder: no gravity, no friction, plain slide move. */
function fullLadderMove(world: CollisionWorld, p: PlayerState) {
  p.oldJump = false;
  const bx = p.baseVel[0];
  const by = p.baseVel[1];
  p.vel[0] += bx;
  p.vel[1] += by;
  slideMove(world, p, p.pos, p.vel, DT);
  p.vel[0] -= bx;
  p.vel[1] -= by;
  p.baseVel[2] = 0;
  // CategorizePosition at the start of the next tick (movetype is not WALK): climbing never grounds.
  if (p.vel[2] > 0) p.ground = null;
  else categorizePosition(world, p);
}

/* ---------------- Water (Source CheckWater / WaterMove / CheckWaterJump / WaterJump) ---------------- */

const WATER_SWIM_UP = 100;
const WATER_SINK = 60;
const WATERJUMP_HEIGHT = 8;
const WATERJUMP_UP = 256;
const waterPoint: Vec3 = [0, 0, 0];
const waterStart: Vec3 = [0, 0, 0];
const waterEnd: Vec3 = [0, 0, 0];
const waterTr = new TraceResult();

const inWater = (world: CollisionWorld, x: number, y: number, z: number) => {
  waterPoint[0] = x;
  waterPoint[1] = y;
  waterPoint[2] = z;
  return world.testPosition(waterPoint, POINT, POINT, MASK_WATER);
};

/** Water level: 0 dry, 1 feet, 2 waist, 3 eyes. Returns true when swimming (waist or deeper). */
function checkWater(world: CollisionWorld, p: PlayerState): boolean {
  const x = p.pos[0];
  const y = p.pos[1];
  const z = p.pos[2];
  p.waterLevel = 0;
  if (inWater(world, x, y, z + HULL_MINS[2] + 1)) {
    p.waterLevel = 1;
    if (inWater(world, x, y, z + (HULL_MINS[2] + hullMaxs(p)[2]) * 0.5)) {
      p.waterLevel = 2;
      if (inWater(world, x, y, z + (p.ducked ? EYE_DUCK : EYE_STAND))) p.waterLevel = 3;
    }
  }
  return p.waterLevel > 1;
}

/** Swimming against a ledge you could stand on: pop up out of the water. */
function checkWaterJump(world: CollisionWorld, p: PlayerState, cmd: Readonly<UserCmd>) {
  if (p.waterJumpTime > 0 || p.vel[2] < -180) return;
  const fx = Math.cos(cmd.yaw);
  const fy = Math.sin(cmd.yaw);
  const vs = Math.hypot(p.vel[0], p.vel[1]);
  // Backing into the water: don't pop forward.
  if (vs !== 0 && (p.vel[0] * fx + p.vel[1] * fy) / vs < 0) return;
  const maxs = hullMaxs(p);
  waterStart[0] = p.pos[0];
  waterStart[1] = p.pos[1];
  waterStart[2] = p.pos[2] + (HULL_MINS[2] + maxs[2]) * 0.5;
  waterEnd[0] = waterStart[0] + fx * 24;
  waterEnd[1] = waterStart[1] + fy * 24;
  waterEnd[2] = waterStart[2];
  let t = world.trace(waterStart, waterEnd, HULL_MINS, maxs, MASK_PLAYERSOLID, waterTr);
  if (t.fraction >= 1) return; // nothing solid at the waist
  const jx = -50 * t.planeNormal[0];
  const jy = -50 * t.planeNormal[1];
  waterStart[2] = p.pos[2] + (p.ducked ? EYE_DUCK : EYE_STAND) + WATERJUMP_HEIGHT;
  waterEnd[2] = waterStart[2];
  t = world.trace(waterStart, waterEnd, HULL_MINS, maxs, MASK_PLAYERSOLID, waterTr);
  if (t.fraction < 1) return; // blocked at eye level
  copy3(waterStart, waterEnd);
  waterEnd[2] -= 1024;
  t = world.trace(waterStart, waterEnd, HULL_MINS, maxs, MASK_PLAYERSOLID, waterTr);
  if (t.fraction < 1 && t.planeNormal[2] >= 0.7) {
    p.vel[2] = WATERJUMP_UP;
    p.oldJump = true; // no jump again until released
    p.waterJumpTime = 2000;
    p.waterJumpVel[0] = jx;
    p.waterJumpVel[1] = jy;
  }
}

/** Swimming: accelerate along the full view direction, water friction, drift down when idle. */
function waterMove(
  world: CollisionWorld,
  p: PlayerState,
  cmd: Readonly<UserCmd>,
  cv: Cvars,
  fmove: number,
  smove: number,
) {
  const sy = Math.sin(cmd.yaw);
  const cy = Math.cos(cmd.yaw);
  const sp = Math.sin(cmd.pitch);
  const cp = Math.cos(cmd.pitch);
  let wx = cp * cy * fmove + sy * smove;
  let wy = cp * sy * fmove - cy * smove;
  let wz = -sp * fmove;
  if (cmd.buttons & IN_JUMP) wz += cv.maxspeed;
  else if (fmove === 0 && smove === 0) wz -= WATER_SINK;
  let wishspeed = Math.hypot(wx, wy, wz);
  if (wishspeed > cv.maxspeed) {
    const k = cv.maxspeed / wishspeed;
    wx *= k;
    wy *= k;
    wz *= k;
    wishspeed = cv.maxspeed;
  }
  wishspeed *= 0.8;
  // Water friction
  const speed = Math.hypot(p.vel[0], p.vel[1], p.vel[2]);
  let newspeed = 0;
  if (speed) {
    newspeed = speed - DT * speed * cv.friction;
    if (newspeed < 0.1) newspeed = 0;
    const k = newspeed / speed;
    p.vel[0] *= k;
    p.vel[1] *= k;
    p.vel[2] *= k;
  }
  if (wishspeed >= 0.1) {
    const addspeed = wishspeed - newspeed;
    if (addspeed > 0) {
      const len = Math.hypot(wx, wy, wz);
      let accelspeed = cv.accelerate * wishspeed * DT;
      if (accelspeed > addspeed) accelspeed = addspeed;
      p.vel[0] += (accelspeed * wx) / len;
      p.vel[1] += (accelspeed * wy) / len;
      p.vel[2] += (accelspeed * wz) / len;
    }
  }
  const bx = p.baseVel[0];
  const by = p.baseVel[1];
  p.vel[0] += bx;
  p.vel[1] += by;
  // Free move: drop onto a step below the destination if there is one; else slide.
  waterEnd[0] = p.pos[0] + p.vel[0] * DT;
  waterEnd[1] = p.pos[1] + p.vel[1] * DT;
  waterEnd[2] = p.pos[2] + p.vel[2] * DT;
  const maxs = hullMaxs(p);
  let t = world.trace(p.pos, waterEnd, HULL_MINS, maxs, MASK_PLAYERSOLID, waterTr);
  let moved = false;
  if (t.fraction === 1) {
    copy3(waterStart, waterEnd);
    waterStart[2] += cv.stepsize + 1;
    t = world.trace(waterStart, waterEnd, HULL_MINS, maxs, MASK_PLAYERSOLID, waterTr);
    if (!t.startSolid && !t.allSolid) {
      copy3(p.pos, t.endPos);
      moved = true;
    }
  }
  if (!moved) slideMove(world, p, p.pos, p.vel, DT);
  p.vel[0] -= bx;
  p.vel[1] -= by;
}

/**
 * Water part of FullWalkMove. Returns true if it handled the tick (swimming or leaping out),
 * false to continue with normal movement.
 */
function waterTick(
  world: CollisionWorld,
  p: PlayerState,
  cmd: Readonly<UserCmd>,
  cv: Cvars,
  fmove: number,
  smove: number,
): boolean {
  const swimming = checkWater(world, p);
  const gravity = p.gravity === 1 ? cv.gravity : cv.gravity * p.gravity;
  if (p.waterJumpTime > 0) {
    // Leaping out of the water.
    if (!swimming) p.vel[2] -= gravity * 0.5 * DT;
    p.waterJumpTime -= 1000 * DT;
    if (p.waterJumpTime <= 0 || p.waterLevel === 0) p.waterJumpTime = 0;
    p.vel[0] = p.waterJumpVel[0];
    p.vel[1] = p.waterJumpVel[1];
    slideMove(world, p, p.pos, p.vel, DT);
    p.ground = null;
    checkWater(world, p);
    return true;
  }
  if (!swimming) return false;
  if (p.waterLevel === 2) checkWaterJump(world, p, cmd);
  const held = (cmd.buttons & IN_JUMP) !== 0;
  if (held && p.waterJumpTime === 0) {
    // Swimming, not jumping.
    p.ground = null;
    p.vel[2] = WATER_SWIM_UP;
  }
  if (!held) p.oldJump = false;
  waterMove(world, p, cmd, cv, fmove, smove);
  categorizePosition(world, p);
  if (p.ground) p.vel[2] = 0;
  const mv = cv.maxvelocity;
  for (let i = 0; i < 3; i++) p.vel[i] = Math.max(-mv, Math.min(mv, p.vel[i]));
  return true;
}

/* ---------------- Slope slide (not CS:S, cv.slopeSlide) ---------------- */

/** Slopes flatter than this (about 11 degrees) are ground as usual. */
const SLIDE_MAX_NZ = 0.98;

/**
 * After categorizePosition: keep the player off the ground while they slide down a slope they
 * fell onto. Reads the ground trace categorizePosition left in `tr`.
 */
function slopeSlide(p: PlayerState, wasGround: boolean, vzBefore: number) {
  if (!p.ground) return;
  const n = tr.planeNormal;
  const startOrKeep = p.sliding || (!wasGround && vzBefore < 0);
  // Moving downhill: the slope normal leans the way you're going.
  if (startOrKeep && n[2] < SLIDE_MAX_NZ && p.vel[0] * n[0] + p.vel[1] * n[1] > 0) {
    p.ground = null;
    p.sliding = true;
  } else p.sliding = false;
}

/**
 * Run one tick of player movement (steps 1-9 of the tick order in docs/MOVEMENT.md).
 * Returns true if the player jumped this tick; `p.jumpSpeed` then holds the takeoff speed.
 */
/*
 * Source CheckStuck: if the player box overlaps something (a float-precision sliver inside two
 * terrain triangles where they meet, say), nudge it out by small offsets. Without this every trace
 * starts in solid and the player freezes. Never triggers when the player isn't overlapping.
 */
const STUCK_OFFSETS: readonly number[] = (() => {
  const out: number[] = [];
  for (const d of [0.125, 0.25, 0.5, 1, 2, 4]) {
    out.push(0, 0, d);
    out.push(d, 0, 0, -d, 0, 0, 0, d, 0, 0, -d, 0);
    out.push(d, 0, d, -d, 0, d, 0, d, d, 0, -d, d);
  }
  out.push(0, 0, -0.125, 0, 0, -0.25, 0, 0, -0.5, 0, 0, -1);
  return out;
})();

function unstick(world: CollisionWorld, p: PlayerState) {
  const maxs = hullMaxs(p);
  if (!world.testPosition(p.pos, HULL_MINS, maxs, MASK_PLAYERSOLID)) return;
  for (let i = 0; i < STUCK_OFFSETS.length; i += 3) {
    tmpPos[0] = p.pos[0] + STUCK_OFFSETS[i];
    tmpPos[1] = p.pos[1] + STUCK_OFFSETS[i + 1];
    tmpPos[2] = p.pos[2] + STUCK_OFFSETS[i + 2];
    if (!world.testPosition(tmpPos, HULL_MINS, maxs, MASK_PLAYERSOLID)) {
      copy3(p.pos, tmpPos);
      return;
    }
  }
}

export function playerMove(
  p: PlayerState,
  cmd: Readonly<UserCmd>,
  world: CollisionWorld,
  cv: Cvars,
  autobhop: boolean,
): boolean {
  unstick(world, p);
  duck(world, p, (cmd.buttons & IN_DUCK) !== 0);

  // Ladders: only on maps that have them, so the normal path stays exactly the same.
  if (world.hasLadders) {
    const l = ladderMove(world, p, cmd);
    if (l === LADDER_ON) {
      fullLadderMove(world, p);
      return false;
    }
    if (l === LADDER_NONE) p.ladder = false;
  }

  // Wish velocity from forward/side move and yaw only.
  let fmove = cmd.forwardmove;
  let smove = cmd.sidemove;
  const raw = Math.hypot(fmove, smove);
  if (raw > cv.maxspeed) {
    const r = cv.maxspeed / raw;
    fmove *= r;
    smove *= r;
  }
  if (p.ducked) {
    fmove *= cv.duckmul;
    smove *= cv.duckmul;
  }
  const sy = Math.sin(cmd.yaw);
  const cy = Math.cos(cmd.yaw);
  // forward = (cos, sin, 0), right = (sin, -cos, 0)
  const wx = cy * fmove + sy * smove;
  const wy = sy * fmove - cy * smove;
  let ws = Math.hypot(wx, wy);
  if (ws > 0) {
    wishdir[0] = wx / ws;
    wishdir[1] = wy / ws;
  } else wishdir[0] = wishdir[1] = 0;
  wishdir[2] = 0;
  if (ws > cv.maxspeed) ws = cv.maxspeed;

  // Water: only on maps that have it, so the normal path stays exactly the same.
  if (world.hasWater && waterTick(world, p, cmd, cv, fmove, smove)) return false;

  // StartGravity (with trigger_gravity scale and the vertical part of a push)
  const gravity = p.gravity === 1 ? cv.gravity : cv.gravity * p.gravity;
  p.vel[2] -= gravity * 0.5 * DT;
  if (p.baseVel[2] !== 0) {
    p.vel[2] += p.baseVel[2] * DT;
    p.baseVel[2] = 0;
  }

  const held = (cmd.buttons & IN_JUMP) !== 0;
  const wantJump = autobhop ? held : held && !p.oldJump;
  let jumped = false;
  if (p.ground && wantJump) {
    p.vel[2] = cv.jumpVelocity;
    p.vel[2] -= gravity * 0.5 * DT; // FinishGravity inside CheckJumpButton
    p.ground = null;
    p.jumpSpeed = horizontalSpeed(p);
    jumped = true;
  }
  p.oldJump = held;

  // Friction is skipped on the tick you jump: ground was just cleared.
  if (p.ground) {
    p.vel[2] = 0;
    friction(p, cv);
  }
  const wasGround = p.ground !== null;
  if (p.ground) walkMove(world, p, cv, wishdir, ws);
  else {
    airAccelerate(p, wishdir, ws, cv.airaccelerate, cv.aircap);
    const bx = p.baseVel[0];
    const by = p.baseVel[1];
    const hasBase = bx !== 0 || by !== 0;
    if (hasBase) {
      p.vel[0] += bx;
      p.vel[1] += by;
    }
    slideMove(world, p, p.pos, p.vel, DT);
    if (hasBase) {
      p.vel[0] -= bx;
      p.vel[1] -= by;
    }
  }
  const vzBefore = p.vel[2];
  categorizePosition(world, p);
  if (cv.slopeSlide) slopeSlide(p, wasGround, vzBefore);
  else p.sliding = false;
  if (!wasGround && p.ground) {
    p.fx.landImpact = Math.max(p.fx.landImpact, -vzBefore);
    p.landSpeed = -vzBefore;
  }

  const mv = cv.maxvelocity;
  for (let i = 0; i < 3; i++) p.vel[i] = Math.max(-mv, Math.min(mv, p.vel[i]));
  if (!p.ground) p.vel[2] -= gravity * 0.5 * DT; // FinishGravity
  if (p.ground) p.vel[2] = 0;
  return jumped;
}

/* ---------------- Noclip (testing aid; remove before release) ---------------- */

const NOCLIP_SPEED = 1000;

/** Fly along the view direction with no collision. Jump goes up, crouch goes down. */
export function noclipMove(p: PlayerState, cmd: Readonly<UserCmd>) {
  const sy = Math.sin(cmd.yaw);
  const cy = Math.cos(cmd.yaw);
  const sp = Math.sin(cmd.pitch);
  const cp = Math.cos(cmd.pitch);
  const fm = Math.sign(cmd.forwardmove);
  const sm = Math.sign(cmd.sidemove);
  const up = (cmd.buttons & IN_JUMP ? 1 : 0) - (cmd.buttons & IN_DUCK ? 1 : 0);
  let wx = cp * cy * fm + sy * sm;
  let wy = cp * sy * fm - cy * sm;
  let wz = -sp * fm + up;
  const len = Math.hypot(wx, wy, wz);
  if (len > 0) {
    wx /= len;
    wy /= len;
    wz /= len;
  }
  const k = Math.min(1, DT * 10);
  p.vel[0] += (wx * NOCLIP_SPEED - p.vel[0]) * k;
  p.vel[1] += (wy * NOCLIP_SPEED - p.vel[1]) * k;
  p.vel[2] += (wz * NOCLIP_SPEED - p.vel[2]) * k;
  p.pos[0] += p.vel[0] * DT;
  p.pos[1] += p.vel[1] * DT;
  p.pos[2] += p.vel[2] * DT;
  p.ground = null;
  p.ladder = false;
  p.ducked = false;
}
